// this hook is responsible for parsing the market and language from the url and providing a function to update them
import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { parseLocaleFromPath, removeLocaleFromPath, replaceLocaleInPath, upsertLocaleInPath } from './path-utils';

export default function useLocaleNavigation() {
  const history = useHistory();
  const location = useLocation();

  const { language, market: pathMarket } = parseLocaleFromPath(window.location.pathname);

  // keeping the market in state to be able to update the url on market change only if language is already defined in the url,
  // otherwise we would lose the language info on market change
  const [market, setMarketState] = useState<string | null>(pathMarket);

  useEffect(() => {
    if (pathMarket) {
      setMarketState(pathMarket);
    }
  }, [pathMarket]);

  const setMarket = useCallback(
    (nextMarket: string) => {
      setMarketState(nextMarket);

      if (!language) return;

      const nextPathname = replaceLocaleInPath(window.location.pathname, `${language}-${nextMarket}`);
      if (!nextPathname) return;

      history.push({
        pathname: nextPathname,
        search: window.location.search
      });
    },
    [history, language]
  );

  const resetLocale = useCallback(() => {
    const pathnameWithoutLocale = removeLocaleFromPath(location.pathname);

    if (pathnameWithoutLocale === location.pathname) return;

    window.history.replaceState(window.history.state, '', `${pathnameWithoutLocale}${location.search}`);
  }, [location.pathname, location.search]);

  const setLanguage = useCallback(
    (nextLanguage: string) => {
      if (!market) return;

      const nextPathname = upsertLocaleInPath(location.pathname, `${nextLanguage}-${market}`);

      history.push({
        pathname: nextPathname,
        search: location.search
      });
    },
    [history, market, location.pathname, location.search]
  );

  return {
    market,
    language,
    setMarket,
    setLanguage,
    resetLocale
  };
}
